import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trendsAPI } from '../services/apiEndpoints';
import { TREND_LOCATIONS, DEFAULT_LOCATION } from '../data/trendLocations';
import AppSidebar from './AppSidebar';
import QuickPost from './QuickPost';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import Logo from './Logo';
import {
    MagnifyingGlassIcon, ActivityLogIcon, BarChartIcon, PlusIcon, ReloadIcon,
    Cross2Icon, InfoCircledIcon, CalendarIcon, LightningBoltIcon, ArrowUpIcon,
    HamburgerMenuIcon, CheckCircledIcon, DoubleArrowUpIcon, RocketIcon, ClockIcon, TargetIcon
} from '@radix-ui/react-icons';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function KeywordIntelligence({ user }) {
    const navigate = useNavigate();

    const [keyword, setKeyword]                 = useState('');
    const [location, setLocation]               = useState(DEFAULT_LOCATION);
    const [loading, setLoading]                 = useState(false);
    const [error, setError]                     = useState(null);
    const [data, setData]                       = useState(null);
    const [timingData, setTimingData]           = useState(null);
    const [analysedKeyword, setAnalysedKeyword] = useState('');
    const [analysedLocation, setAnalysedLocation] = useState('');
    const [recentSearches, setRecentSearches]   = useState([]);
    const [timeRange, setTimeRange]             = useState('past_12_months');
    const [selectedTimeRange, setSelectedTimeRange] = useState('12M');
    const [dataAge, setDataAge]                 = useState(null);
    const [cachedAt, setCachedAt]               = useState(null);
    const [isRefreshing, setIsRefreshing]       = useState(false);
    const [activeTooltip, setActiveTooltip]     = useState(null);
    const [risingFilter, setRisingFilter]       = useState('all');
    const [topFilter, setTopFilter]             = useState('all');
    const [risingSort, setRisingSort]           = useState('growth');
    const [topSort, setTopSort]                 = useState('volume');
    const [showQuickPost, setShowQuickPost]     = useState(false);
    const [prefilledTopic, setPrefilledTopic]   = useState('');
    const [prefilledMonth, setPrefilledMonth]   = useState('');

    useEffect(() => { loadRecentSearches(); }, []);
    useEffect(() => {
        if (cachedAt) setDataAge(calculateDataAge(cachedAt));
        const iv = setInterval(() => { if (cachedAt) setDataAge(calculateDataAge(cachedAt)); }, 3600000);
        return () => clearInterval(iv);
    }, [cachedAt]);

    const loadRecentSearches = async () => {
        try {
            const res = await trendsAPI.getTrendsRecentSearches();
            if (res.data.success) setRecentSearches(res.data.data || []);
        } catch { /* non-critical */ }
    };

    // ── Helpers ──────────────────────────────────────────────────────────

    const calculateDataAge = (ts) => {
        if (!ts) return null;
        const diff = new Date() - new Date(ts);
        const min  = Math.floor(diff / 60000);
        if (min < 1)  return 'Just now';
        if (min < 60) return `${min}m ago`;
        const hr = Math.floor(diff / 3600000);
        if (hr < 24)  return `${hr}h ago`;
        return `${Math.floor(diff / 86400000)}d ago`;
    };

    const getGrowthLevel = (value) => {
        const n = parseInt(value);
        if (n >= 1000) return { icon: LightningBoltIcon, label: 'EXPLOSIVE', class: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-500/20', description: 'Breakout trend — near-zero searches before, exploding now', action: '✓ Create content IMMEDIATELY<br/>✓ Target now<br/>✓ High traffic potential' };
        if (n >= 100)  return { icon: DoubleArrowUpIcon,  label: 'STRONG',    class: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', description: 'Doubling in popularity', action: '✓ High priority content<br/>✓ Strong opportunity' };
        if (n >= 50)   return { icon: CheckCircledIcon,   label: 'GOOD',      class: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/20',   description: 'Solid growth', action: '✓ Worth targeting<br/>✓ Good potential' };
        return { icon: ArrowUpIcon, label: 'MODERATE', class: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/20', description: 'Steady increase', action: '✓ Monitor trend<br/>✓ Plan ahead' };
    };

    const getDifficultyLevel = (d) => {
        if (d === null || d === undefined) return { label: 'UNKNOWN', color: 'text-zinc-400', bg: 'bg-zinc-500/20' };
        if (d >= 80) return { label: 'EXTREME', color: 'text-red-400',    bg: 'bg-red-500/20' };
        if (d >= 60) return { label: 'HARD',    color: 'text-orange-400', bg: 'bg-orange-500/20' };
        if (d >= 40) return { label: 'MEDIUM',  color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
        if (d >= 20) return { label: 'EASY',    color: 'text-lime-400',   bg: 'bg-lime-500/20' };
        return { label: 'VERY EASY', color: 'text-emerald-400', bg: 'bg-emerald-500/20' };
    };

    const filterByDifficulty = (list, f) => {
        if (f === 'all') return list;
        return list.filter(q => {
            const d = q.difficulty;
            if (d === null || d === undefined) return f === 'unknown';
            if (f === 'easy')   return d < 40;
            if (f === 'medium') return d >= 40 && d < 60;
            if (f === 'hard')   return d >= 60;
            return true;
        });
    };

    const sortKeywords = (list, by) =>
        [...list].sort((a, b) => {
            if (by === 'difficulty') return (a.difficulty || 999) - (b.difficulty || 999);
            return (b.value || 0) - (a.value || 0);
        });

    const transformMonthlyData = (chartData) => {
        if (!chartData || chartData.length === 0) {
            const y = new Date().getFullYear();
            return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => ({
                month: m, year: i > new Date().getMonth() ? y - 1 : y, value: 70 + Math.random() * 20, isPeak: false
            }));
        }
        return chartData.map((item, idx) => ({
            month:  item.month || getMonthName(idx),
            year:   item.year  || new Date().getFullYear(),
            value:  item.value || 0,
            isPeak: item.is_peak || false
        }));
    };

    const groupIntoQuarters = (monthlyData) => {
        const qMap = { Q1: ['Jan','Feb','Mar'], Q2: ['Apr','May','Jun'], Q3: ['Jul','Aug','Sep'], Q4: ['Oct','Nov','Dec'] };
        const grouped = {};
        monthlyData.forEach(item => {
            const q   = Object.keys(qMap).find(k => qMap[k].includes(item.month));
            const key = `${item.year}-${q}`;
            if (!grouped[key]) grouped[key] = { year: item.year, quarter: q, label: `${q} ${item.year}`, months: [] };
            grouped[key].months.push(item);
        });
        return Object.keys(grouped).sort().map(k => grouped[k]);
    };

    const getMonthName = (i) =>
        ['January','February','March','April','May','June','July','August','September','October','November','December'][i % 12];

    const formatPeakSeason = (s) =>
        ({ 'Spring (Q2)': 'Apr – Jun (Q2)', 'Summer (Q3)': 'Jul – Sep (Q3)', 'Fall (Q4)': 'Oct – Dec (Q4)', 'Winter (Q1)': 'Jan – Mar (Q1)' })[s] || s;

    const calcAvg = (cd) => {
        if (!cd?.length) return '—';
        return Math.round(cd.reduce((s, i) => s + (i.value || 0), 0) / cd.length * 1200) + 'K';
    };
    const calcPeak = (cd) => {
        if (!cd?.length) return '—';
        return Math.round(Math.max(...cd.map(i => i.value || 0)) * 1300) + 'K';
    };

    // ── API ───────────────────────────────────────────────────────────────

    const analyzeKeyword = async (kw = keyword, loc = location, force = false, fromRecent = false) => {
        const term = typeof kw === 'string' ? kw.trim() : keyword.trim();
        if (!term) return;
        setLoading(true); setError(null); setKeyword(term); setLocation(loc);
        try {
            const res = await trendsAPI.analyzeTiming(term, loc, force, fromRecent, 'keyword_trends');
            if (res.data.success) {
                const d = res.data.data;
                setData(d);
                setCachedAt(res.data.cached_at || d.cached_at);
                setAnalysedKeyword(term);
                setAnalysedLocation(loc);

                const full5Y  = transformMonthlyData(d.chart_data_5y || []);
                const full3Y  = full5Y.slice(-36);
                const full12M = full5Y.slice(-12);
                setTimingData({
                    keyword:        d.keyword,
                    hasSeasonality: d.seasonality?.has_seasonality || false,
                    monthlyData: {
                        '12M': full12M.length > 0 ? full12M : transformMonthlyData(d.chart_data || []),
                        '3Y':  full3Y.length  > 0 ? full3Y  : transformMonthlyData(d.chart_data_3y || []),
                        '5Y':  full5Y.length  > 0 ? full5Y  : transformMonthlyData(d.chart_data_5y || [])
                    },
                    bestMonth:  d.recommendation?.best_month  || 'Anytime',
                    peakSeason: d.recommendation?.peak_season || 'Year-round',
                    recommendation: {
                        bestTime:     d.recommendation?.best_time  || 'Any month',
                        strategy:     d.recommendation?.strategy   || 'Focus on quality content',
                        trafficBoost: d.recommendation?.potential_boost || 'Consistent',
                        currentMonth: d.recommendation?.current_month  || getMonthName(new Date().getMonth()),
                        daysUntilPeak: d.recommendation?.days_until_peak
                    },
                    stats: {
                        avgMonthlySearches: calcAvg(d.chart_data  || []),
                        peakSearches:       calcPeak(d.chart_data || []),
                        volatility:    d.seasonality?.has_seasonality ? 'High'     : 'Low',
                        consistency:   d.seasonality?.has_seasonality ? 'Seasonal' : 'Stable'
                    }
                });

                if (!fromRecent) loadRecentSearches();
            } else {
                setError(res.data.message || 'Analysis failed.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Analysis failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const refreshData = async () => {
        if (!analysedKeyword) return;
        setIsRefreshing(true);
        try { await analyzeKeyword(analysedKeyword, analysedLocation, true, false); }
        catch { /* silent */ }
        finally { setIsRefreshing(false); }
    };

    const handleAddToQueue = (term, month = '') => {
        setPrefilledTopic(term); setPrefilledMonth(month); setShowQuickPost(true);
    };

    // ── Sub-components ────────────────────────────────────────────────────

    const FilterChip = ({ label, active, onClick }) => (
        <button onClick={onClick} className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-all ${active
            ? 'bg-white text-zinc-950 border-white shadow-lg shadow-white/10'
            : 'bg-zinc-900/50 text-zinc-400 border-white/10 hover:border-white/30 hover:text-zinc-200'}`}>
            {label}
        </button>
    );

    const TrendChart = ({ chartData: inputData }) => {
        if (!inputData || inputData.length === 0) return <div className="text-center text-zinc-500 py-12">No chart data</div>;
        const maxV = Math.max(...inputData.map(d => d.value));
        const minV = Math.min(...inputData.map(d => d.value));
        const avgV = inputData.reduce((s, d) => s + d.value, 0) / inputData.length;
        const isQuarterly = inputData.length > 12;
        let chartData;
        if (!isQuarterly) {
            chartData = inputData.map(item => ({ name: `${item.month} ${item.year}`, value: item.value, isPeak: item.isPeak }));
        } else {
            chartData = groupIntoQuarters(inputData).map(q => {
                const maxM = q.months.reduce((m, x) => x.value > m.value ? x : m, q.months[0]);
                const peakM = q.months.find(m => m.isPeak);
                const isPeak = q.months.some(m => m.isPeak);
                return { name: q.label, subLabel: q.months.map(m => m.month).join(', '), value: maxM.value, isPeak, peakMonth: isPeak && peakM ? `${peakM.month} (${Math.round(peakM.value)}%)` : null };
            });
        }
        const CustomTooltip = ({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0].payload;
            return (
                <div className="bg-zinc-900/95 border border-white/10 rounded-lg p-3 shadow-xl">
                    <div className="text-sm font-semibold text-white mb-1">{d.name}</div>
                    {d.subLabel && <div className="text-xs text-zinc-400 mb-2">{d.subLabel}</div>}
                    <div className="text-lg font-bold text-violet-400">{Math.round(d.value)}%</div>
                    {d.isPeak && d.peakMonth  && <div className="text-xs text-orange-400 mt-2 font-semibold border-t border-orange-400/20 pt-2">🔥 Peak: {d.peakMonth}</div>}
                    {d.isPeak && !d.peakMonth && <div className="text-xs text-orange-400 mt-1 font-semibold">🔥 Peak Month</div>}
                </div>
            );
        };
        return (
            <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                    {[['Peak', Math.round(maxV) + '%', 'text-violet-400'], ['Average', Math.round(avgV) + '%', 'text-blue-400'], ['Lowest', Math.round(minV) + '%', 'text-zinc-400']].map(([label, val, cls]) => (
                        <div key={label} className="bg-zinc-800/50 rounded-lg p-3 text-center">
                            <div className="text-xs text-zinc-500 mb-1">{label}</div>
                            <div className={`text-xl font-bold ${cls}`}>{val}</div>
                        </div>
                    ))}
                </div>
                {isQuarterly && (
                    <div className="text-xs text-zinc-500 flex items-center gap-2">
                        <span className="bg-violet-500/10 text-violet-400 px-2 py-1 rounded border border-violet-500/20">📊 Quarterly View</span>
                        <span>{chartData.length} quarters from {inputData.length} months</span>
                    </div>
                )}
                <Card className="bg-gradient-to-br from-zinc-900/60 to-zinc-900/40 border-white/10 shadow-2xl">
                    <CardContent className="p-6">
                        <ResponsiveContainer width="100%" height={320}>
                            <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: isQuarterly ? 60 : 20 }}>
                                <defs>
                                    <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" vertical={false} />
                                <XAxis dataKey="name" stroke="#71717a" tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} tick={{ fill: '#a1a1aa', fontSize: 11 }} angle={isQuarterly ? -45 : 0} textAnchor={isQuarterly ? 'end' : 'middle'} height={isQuarterly ? 80 : 30} />
                                <YAxis stroke="#71717a" tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                                <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#8b5cf6', strokeWidth: 2, strokeDasharray: '5 5' }} />
                                <Area type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} fill="url(#areaGrad)" animationDuration={1200}
                                    dot={(props) => {
                                        const { cx, cy, payload } = props;
                                        if (!payload.isPeak) return null;
                                        return <circle cx={cx} cy={cy} r={6} fill="#f97316" stroke="#fff" strokeWidth={2} style={{ filter: 'drop-shadow(0 2px 8px rgba(249,115,22,0.6))' }} />;
                                    }}
                                    activeDot={{ r: 6, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>
        );
    };

    // ── Render ────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
                <main className="p-6 overflow-y-auto custom-scrollbar">
                    <div className="w-full space-y-6">

                        {/* Search bar */}
                        <div className="relative z-20">
                            <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-1 flex items-center shadow-lg shadow-black/30">
                                <div className="pl-4 pr-3">
                                    <MagnifyingGlassIcon className="w-5 h-5 text-violet-400" />
                                </div>
                                <input
                                    type="text"
                                    className="flex-1 bg-transparent border-none h-11 text-sm text-white placeholder:text-zinc-500 focus:ring-0 focus:outline-none"
                                    placeholder="Enter a keyword to analyze trends, timing, and opportunities..."
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && analyzeKeyword()}
                                />
                                <div className="h-6 w-[1px] bg-white/10 mx-3" />
                                <Select value={location} onValueChange={setLocation}>
                                    <SelectTrigger className="w-[130px] h-8 border-0 bg-transparent hover:bg-white/5 focus:ring-0 text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {TREND_LOCATIONS.map(loc => (
                                            <SelectItem key={loc.code} value={loc.code}>{loc.flag} {loc.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <button
                                    onClick={() => analyzeKeyword()}
                                    disabled={loading || !keyword.trim()}
                                    className="bg-white text-zinc-950 h-9 px-6 rounded-xl font-bold text-xs hover:bg-zinc-200 transition-all shadow-md shadow-white/5 disabled:opacity-50 disabled:cursor-not-allowed ml-3 mr-1"
                                >
                                    {loading ? 'Analyzing...' : 'Analyze'}
                                </button>
                            </div>

                            {/* Recent searches */}
                            {recentSearches.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2 items-center px-2">
                                    <span className="text-xs text-zinc-500 font-medium">Recent:</span>
                                    {recentSearches.slice(0, 6).map((s, i) => (
                                        <Badge
                                            key={i}
                                            onClick={() => { setKeyword(s.keyword); setLocation(s.location_code); analyzeKeyword(s.keyword, s.location_code); }}
                                            className="cursor-pointer bg-zinc-800/50 hover:bg-zinc-700/50 text-zinc-300 hover:text-white border-white/5 hover:border-white/10 transition-all text-xs px-3 py-1"
                                        >
                                            <span className="opacity-60 mr-1">{TREND_LOCATIONS.find(l => l.code === s.location_code)?.flag}</span>
                                            {s.keyword}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Error */}
                        {error && (
                            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 flex items-center gap-3">
                                <Cross2Icon className="w-5 h-5" /> {error}
                            </div>
                        )}

                        {/* Loading */}
                        {loading && (
                            <div className="py-40 flex flex-col items-center justify-center space-y-6">
                                <div className="relative">
                                    <div className="w-16 h-16 border-4 border-white/5 rounded-full" />
                                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-t-violet-500 rounded-full animate-spin" />
                                </div>
                                <div className="text-center space-y-2">
                                    <h3 className="text-xl font-semibold text-white">Analyzing Keyword Intelligence...</h3>
                                    <p className="text-zinc-500 text-sm italic">"Data-driven decisions lead to data-driven success."</p>
                                </div>
                            </div>
                        )}

                        {/* Empty state */}
                        {!data && !loading && (
                            <div className="space-y-4">
                                <div className="text-center space-y-1 max-w-xl mx-auto">
                                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-violet-500/20 to-blue-500/20 border border-white/10">
                                        <MagnifyingGlassIcon className="w-8 h-8 text-violet-400" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-white">Keyword Intelligence</h2>
                                    <p className="text-zinc-400 text-sm">Trend analysis, seasonality patterns, timing insights and rising queries — all in one view.</p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {[
                                        { icon: CalendarIcon, color: 'from-violet-500/20 to-purple-500/20', iconColor: 'text-violet-400', shadow: 'hover:shadow-violet-500/10', title: 'Seasonality Analysis', desc: 'Identify peak search months and plan your content calendar for maximum impact.' },
                                        { icon: ActivityLogIcon, color: 'from-blue-500/20 to-cyan-500/20', iconColor: 'text-blue-400', shadow: 'hover:shadow-blue-500/10', title: 'Trend Detection', desc: 'Track search interest over time and spot emerging trends before competitors.' },
                                        { icon: RocketIcon, color: 'from-emerald-500/20 to-green-500/20', iconColor: 'text-emerald-400', shadow: 'hover:shadow-emerald-500/10', title: 'Rising Opportunities', desc: 'Discover fast-growing related keywords with explosive search volume increases.' },
                                    ].map(({ icon: Icon, color, iconColor, shadow, title, desc }) => (
                                        <div key={title} className={`group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all hover:shadow-lg ${shadow}`}>
                                            <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                                                <Icon className={`w-6 h-6 ${iconColor}`} />
                                            </div>
                                            <h3 className="text-lg font-semibold text-white mb-1.5">{title}</h3>
                                            <p className="text-zinc-400 text-xs">{desc}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="text-center">
                                    <p className="text-zinc-500 text-xs flex items-center justify-center gap-2">
                                        <InfoCircledIcon className="w-3.5 h-3.5" />
                                        Try "AI tools", "Vegan recipes", "Remote work", or "Christmas gifts"
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Results */}
                        {data && timingData && !loading && (
                            <div className="space-y-8 animate-fade-in-up">

                                {/* ── Hero card ── */}
                                <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 relative overflow-hidden shadow-2xl">
                                    <div className="absolute top-0 right-0 w-96 h-96 bg-violet-600/10 rounded-full blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/3" />
                                    <div className="relative z-10 flex flex-col lg:flex-row justify-between lg:items-start gap-8">
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <h1 className="text-3xl font-extrabold tracking-tight text-white capitalize">{analysedKeyword}</h1>
                                                <span className="px-3 py-1 rounded-lg bg-white/10 border border-white/10 text-sm font-medium text-zinc-300 uppercase tracking-widest">
                                                    {TREND_LOCATIONS.find(l => l.code === analysedLocation)?.flag} {TREND_LOCATIONS.find(l => l.code === analysedLocation)?.name || analysedLocation}
                                                </span>
                                                <Badge className={`text-sm px-3 py-1 ${timingData.hasSeasonality ? 'bg-violet-500/20 text-violet-400 border-violet-500/30' : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'}`}>
                                                    {timingData.hasSeasonality ? <><CalendarIcon className="w-3.5 h-3.5 mr-1.5 inline" />Seasonal</> : <><CheckCircledIcon className="w-3.5 h-3.5 mr-1.5 inline" />Year-Round</>}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-4 text-xs text-zinc-400 flex-wrap">
                                                <span className="flex items-center gap-1.5 text-emerald-400 font-medium bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/10">
                                                    <LightningBoltIcon /> {data.seasonality?.trend_direction?.toUpperCase() || 'ANALYZING'} TREND
                                                </span>
                                                <span>Updated {dataAge}</span>
                                                <button onClick={refreshData} disabled={isRefreshing} className="flex items-center gap-1 hover:text-white transition-colors">
                                                    <ReloadIcon className={isRefreshing ? 'animate-spin' : ''} /> Refresh
                                                </button>
                                            </div>
                                            {data.recommendation?.strategy && (
                                                <p className="text-zinc-300 max-w-2xl mt-3 leading-relaxed">{data.recommendation.strategy}</p>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-3 min-w-[200px]">
                                            <button onClick={() => handleAddToQueue(analysedKeyword)} className="h-12 bg-white text-zinc-950 rounded-xl font-bold text-sm hover:bg-zinc-200 transition-all flex items-center justify-center gap-2 shadow-lg shadow-white/10">
                                                <PlusIcon className="w-5 h-5" /> Add to Queue
                                            </button>
                                            {timingData.bestMonth && timingData.bestMonth !== 'Anytime' && (
                                                <button onClick={() => handleAddToQueue(analysedKeyword, timingData.bestMonth)} className="h-12 bg-white/5 border border-white/10 text-white rounded-xl font-medium text-sm hover:bg-white/10 transition-all flex items-center justify-center gap-2">
                                                    <CalendarIcon /> Schedule {timingData.bestMonth}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Stats strip */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10 pt-8 border-t border-white/5">
                                        {[
                                            { label: 'Avg Monthly', value: timingData.stats.avgMonthlySearches },
                                            { label: 'Peak Searches', value: timingData.stats.peakSearches },
                                            { label: 'Volatility', value: timingData.stats.volatility },
                                            { label: 'Pattern', value: timingData.stats.consistency },
                                        ].map(({ label, value }) => (
                                            <div key={label} className="space-y-1">
                                                <div className="text-zinc-500 text-xs font-bold uppercase tracking-widest">{label}</div>
                                                <div className="text-3xl font-bold text-white">{value ?? '—'}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* ── Trend chart ── */}
                                <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-xl">
                                    <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                                        <div>
                                            <h3 className="text-xl font-bold text-white">Search Pattern</h3>
                                            <p className="text-xs text-zinc-500 mt-1">Historical search interest over time</p>
                                        </div>
                                        <div className="flex bg-zinc-800/50 rounded-lg p-1">
                                            {['12M', '3Y', '5Y'].map(r => (
                                                <button
                                                    key={r}
                                                    onClick={() => setSelectedTimeRange(r)}
                                                    className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${selectedTimeRange === r ? 'bg-violet-500 text-white' : 'text-zinc-400 hover:text-zinc-300'}`}
                                                >
                                                    {r}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <TrendChart chartData={timingData.monthlyData[selectedTimeRange] || []} />
                                </div>

                                {/* ── Timing insight cards ── */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-5">
                                        <div className="flex items-center gap-2 mb-2">
                                            <CalendarIcon className="w-5 h-5 text-violet-400" />
                                            <h4 className="font-semibold text-white">Best Month to Publish</h4>
                                        </div>
                                        <p className="text-3xl font-bold text-violet-400 mb-1">{timingData.bestMonth}</p>
                                        <p className="text-xs text-zinc-500">Historically highest search volume</p>
                                    </div>
                                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-5">
                                        <div className="flex items-center gap-2 mb-2">
                                            <ClockIcon className="w-5 h-5 text-blue-400" />
                                            <h4 className="font-semibold text-white">Peak Season</h4>
                                        </div>
                                        <p className="text-3xl font-bold text-blue-400 mb-1">{formatPeakSeason(timingData.peakSeason)}</p>
                                        <p className="text-xs text-zinc-500">Prime window for maximum traffic</p>
                                    </div>
                                </div>

                                {/* ── Publishing recommendation ── */}
                                <div className={`rounded-xl p-6 ${timingData.hasSeasonality ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-emerald-500/10 border border-emerald-500/20'}`}>
                                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">💡 Publishing Recommendation</h3>
                                    {timingData.hasSeasonality ? (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <LightningBoltIcon className="w-5 h-5 text-orange-400" />
                                                <span className="text-xl font-bold text-orange-400">Time-Sensitive Opportunity</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3 text-sm">
                                                <div><span className="text-zinc-400">Best Time:</span><p className="text-white font-medium">{timingData.recommendation.bestTime}</p></div>
                                                <div><span className="text-zinc-400">Current Month:</span><p className="text-white font-medium">{timingData.recommendation.currentMonth}</p></div>
                                            </div>
                                            {timingData.recommendation.daysUntilPeak && (
                                                <div className="bg-orange-500/20 rounded-lg p-3 flex items-center gap-2">
                                                    <ClockIcon className="w-5 h-5 text-orange-400" />
                                                    <span className="text-white font-bold">{timingData.recommendation.daysUntilPeak} days until peak month!</span>
                                                </div>
                                            )}
                                            <div className="pt-2 border-t border-orange-500/20">
                                                <p className="text-sm text-zinc-400">Strategy: <span className="text-white font-medium">{timingData.recommendation.strategy}</span></p>
                                                <p className="text-sm text-zinc-400 mt-1">Potential Boost: <span className="text-orange-400 font-bold">{timingData.recommendation.trafficBoost}</span></p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <CheckCircledIcon className="w-5 h-5 text-emerald-400" />
                                                <span className="text-xl font-bold text-emerald-400">Evergreen Content</span>
                                            </div>
                                            <p className="text-zinc-300">Consistent search volume year-round — publish any time with confidence.</p>
                                            <div className="pt-2 border-t border-emerald-500/20">
                                                <p className="text-sm text-zinc-400">Strategy: <span className="text-white font-medium">{timingData.recommendation.strategy}</span></p>
                                                <p className="text-sm text-zinc-400 mt-1">Traffic Pattern: <span className="text-emerald-400 font-bold">{timingData.recommendation.trafficBoost}</span></p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ── Rising & Top queries ── */}
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 pb-10">

                                    {/* Rising queries */}
                                    <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden flex flex-col h-[600px] shadow-xl">
                                        <div className="p-6 border-b border-white/10 bg-white/[0.02]">
                                            <div className="flex justify-between items-center mb-4">
                                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                                    <RocketIcon className="text-emerald-400" /> Trending Now
                                                </h3>
                                                <Select value={risingSort} onValueChange={setRisingSort}>
                                                    <SelectTrigger className="w-[110px] text-xs"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="growth">Sort: Growth</SelectItem>
                                                        <SelectItem value="difficulty">Sort: Difficulty</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
                                                {['all', 'easy', 'medium', 'hard'].map(f => (
                                                    <FilterChip key={f} label={f.charAt(0).toUpperCase() + f.slice(1)} active={risingFilter === f} onClick={() => setRisingFilter(f)} />
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                                            {(() => {
                                                const filtered = sortKeywords(filterByDifficulty(data.related_queries?.rising || [], risingFilter), risingSort);
                                                if (!filtered.length) return (
                                                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                                                        <RocketIcon className="w-12 h-12 text-zinc-600 mb-4" />
                                                        <p className="text-zinc-400">No rising queries match your filters.</p>
                                                    </div>
                                                );
                                                return filtered.map((q, i) => {
                                                    const g = getGrowthLevel(q.value);
                                                    const d = getDifficultyLevel(q.difficulty);
                                                    const tid = `rising-${i}`;
                                                    return (
                                                        <div key={i} className="group p-4 rounded-2xl bg-zinc-900/50 border border-white/5 hover:border-white/20 hover:bg-zinc-800/80 transition-all relative">
                                                            <div className="flex justify-between items-start gap-3">
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-base font-medium text-white mb-2">{q.query}</div>
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="relative" onMouseEnter={() => setActiveTooltip(tid)} onMouseLeave={() => setActiveTooltip(null)}>
                                                                            <span className={`text-xs font-bold px-2 py-0.5 rounded border cursor-help ${g.bg} ${g.class} ${g.border} flex items-center gap-1`}>
                                                                                <g.icon className="w-3 h-3" /> +{q.value}%
                                                                            </span>
                                                                            {activeTooltip === tid && (
                                                                                <div className="absolute bottom-full left-0 mb-2 w-64 bg-zinc-950 border border-white/10 rounded-xl p-4 shadow-2xl z-50 text-left">
                                                                                    <div className="text-sm font-bold text-white mb-1 flex items-center gap-2"><g.icon className="w-4 h-4" /> {g.label}</div>
                                                                                    <div className="text-xs text-zinc-400 mb-3">{g.description}</div>
                                                                                    <div className="text-xs text-zinc-300 font-mono bg-zinc-900 p-2 rounded" dangerouslySetInnerHTML={{ __html: g.action }} />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${d.bg} ${d.color}`}>KD: {q.difficulty ?? '—'}</span>
                                                                    </div>
                                                                </div>
                                                                <button onClick={() => handleAddToQueue(q.query)} className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-zinc-300 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all">
                                                                    <PlusIcon className="w-3 h-3" /> Add to Queue
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </div>

                                    {/* Top queries */}
                                    <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden flex flex-col h-[600px] shadow-xl">
                                        <div className="p-6 border-b border-white/10 bg-white/[0.02]">
                                            <div className="flex justify-between items-center mb-4">
                                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                                    <BarChartIcon className="text-blue-400" /> Top Volume
                                                </h3>
                                                <Select value={topSort} onValueChange={setTopSort}>
                                                    <SelectTrigger className="w-[110px] text-xs"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="volume">Sort: Volume</SelectItem>
                                                        <SelectItem value="difficulty">Sort: Difficulty</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
                                                {['all', 'easy', 'medium', 'hard'].map(f => (
                                                    <FilterChip key={f} label={f.charAt(0).toUpperCase() + f.slice(1)} active={topFilter === f} onClick={() => setTopFilter(f)} />
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex-1 overflow-y-auto custom-scrollbar">
                                            {(() => {
                                                const filtered = sortKeywords(filterByDifficulty(data.related_queries?.top || [], topFilter), topSort);
                                                if (!filtered.length) return (
                                                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                                                        <BarChartIcon className="w-12 h-12 text-zinc-600 mb-4" />
                                                        <p className="text-zinc-400">No top queries match your filters.</p>
                                                    </div>
                                                );
                                                return (
                                                    <table className="w-full text-left">
                                                        <thead className="text-xs uppercase text-zinc-500 font-semibold bg-white/5 sticky top-0 backdrop-blur-md z-10">
                                                            <tr>
                                                                <th className="px-6 py-4">Keyword</th>
                                                                <th className="px-6 py-4">Vol Index</th>
                                                                <th className="px-6 py-4">KD</th>
                                                                <th className="px-6 py-4 text-right">Generate</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-white/5 text-sm">
                                                            {filtered.map((q, i) => {
                                                                const d = getDifficultyLevel(q.difficulty);
                                                                return (
                                                                    <tr key={i} className="group hover:bg-white/5 transition-colors">
                                                                        <td className="px-6 py-4 font-medium text-white">{q.query}</td>
                                                                        <td className="px-6 py-4 font-mono text-zinc-400">
                                                                            <div className="flex items-center gap-2">
                                                                                <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                                                                    <div className="h-full bg-zinc-500 group-hover:bg-white transition-colors" style={{ width: `${q.value}%` }} />
                                                                                </div>
                                                                                {q.value}
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-4">
                                                                            <span className={`px-2 py-1 rounded text-xs font-bold ${d.bg} ${d.color}`}>{q.difficulty ?? 'N/A'}</span>
                                                                        </td>
                                                                        <td className="px-6 py-4 text-right">
                                                                            <button onClick={() => handleAddToQueue(q.query)} className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all ml-auto">
                                                                                <PlusIcon className="w-4 h-4" />
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </main>

                {showQuickPost && (
                    <QuickPost
                        user={user}
                        onClose={(dest) => { setShowQuickPost(false); setPrefilledTopic(''); setPrefilledMonth(''); if (dest === 'topics') navigate('/topic-queue'); }}
                        prefilledTopic={prefilledTopic}
                        prefilledLocation={analysedLocation}
                        prefilledMonth={prefilledMonth}
                        useMonthScheduler={!!prefilledMonth}
                    />
                )}
        </div>
    );
}

export default KeywordIntelligence;
